import React, { useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { BodyLarge400, BodyLarge500, TitleLarge500, TitleSmall500 } from '@nebula/global-styles';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import GUIDANCE_MESSAGES from '../PaymentMethodAuthorization/constants/messaging';
import AuthorizationPrompt from '../PaymentMethodAuthorization/AuthorizationPrompt';
import { Header, Main, Review, Methods } from '../PaymentCaptureMethod/index.styles';
import { Guidance } from '../PaymentMethodAuthorization/index.styles';

const AuthorizationScreen = ({ actionTokenId, onToken, onError, isWebview }) => {
  const guidanceRef = useRef(null);
  const planPrice = useSelector(state => state.billing?.signupPrice);
  const selectedBillDate = useSelector(state => state.billingDateSelection?.selectedBillDate);

  const billingSummary = useMemo(() => {
    if (typeof planPrice !== 'number' || !selectedBillDate) {
      return null;
    }
    const formattedPrice = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(planPrice);

    const formattedDate = dayjs(selectedBillDate).format('D MMMM YYYY');

    return `You’ll go back to a monthly subscription of ${formattedPrice} from ${formattedDate}.`;
  }, [planPrice, selectedBillDate]);

  useLayoutEffect(() => {
    guidanceRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <React.Fragment>
      <Header>
        {!isWebview && <TitleLarge500 as="h1">Change billing date</TitleLarge500>}
        {billingSummary && <BodyLarge400>{billingSummary}</BodyLarge400>}
      </Header>
      <Main>
        <Review>
          <Guidance ref={guidanceRef}>
            <TitleSmall500>Confirm your card details</TitleSmall500>
            <BodyLarge400 as="span">{`${GUIDANCE_MESSAGES.HEADING} `}</BodyLarge400>
            <BodyLarge500 as="span">
              {isWebview ? GUIDANCE_MESSAGES.WARNING_APP : GUIDANCE_MESSAGES.WARNING_WEB}
            </BodyLarge500>
          </Guidance>
        </Review>
        <Methods>
          <AuthorizationPrompt actionTokenId={actionTokenId} onToken={onToken} onError={onError} />
        </Methods>
      </Main>
    </React.Fragment>
  );
};

AuthorizationScreen.propTypes = {
  actionTokenId: PropTypes.string.isRequired,
  onToken: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  isWebview: PropTypes.bool.isRequired,
};

export default AuthorizationScreen;
